import { useEffect, useState } from "react";
import {
  Shield, Users, Skull, Wrench, Radio, Flame, Crosshair, Zap, Heart,
  Compass, Map, Package, Swords, Target, Moon, Sun, Cloud, Eye, Lock,
  Key, Flag,
} from "lucide-react";
import { useReveal } from "../../hooks/useParallax";
import { api } from "../../lib/api";

// Allow-list mirrors backend `FEATURE_ALLOWED_ICONS`. Kept as a string→
// component map because we store the icon name in Mongo (portable across
// versions of lucide-react and forward-compatible if we swap icon libs).
const ICON_MAP = {
  Shield, Users, Skull, Wrench, Radio, Flame, Crosshair, Zap, Heart,
  Compass, Map, Package, Swords, Target, Moon, Sun, Cloud, Eye, Lock,
  Key, Flag,
};

// Client-side defaults used before the fetch resolves so the section
// doesn't flash empty on first paint. The Faction Warfare card now
// points at the new user-supplied artwork (armed survivors, black van).
const FALLBACK_FEATURES = [
  { icon: "Shield", title: "Story-First Permadeath",
    body: "Permadeath on your terms — bury the name or wear it as a scar. Burd Journals carry your character forward either way. Story first, stats second.",
    span: "md:col-span-7", icon_color: "#C0A875",
    image: "https://images.pexels.com/photos/30663241/pexels-photo-30663241.jpeg?auto=compress&cs=tinysrgb&w=1200",
    image_position: "center", image_size: "cover" },
  { icon: "Users", title: "Character-First RP",
    body: "Voice or text. Stay in character at all costs. Drama is earned, not announced.",
    span: "md:col-span-5", icon_color: "#C0A875",
    image: "https://images.pexels.com/photos/1212487/pexels-photo-1212487.jpeg?auto=compress&cs=tinysrgb&w=1200",
    image_position: "center", image_size: "cover" },
  { icon: "Wrench", title: "Tuned Sandbox",
    body: "Slower xp, harsher winters, sparse loot. Every nail matters.",
    span: "md:col-span-6", icon_color: "#C0A875",
    image: "https://images.pexels.com/photos/2902440/pexels-photo-2902440.jpeg?auto=compress&cs=tinysrgb&w=1200",
    image_position: "center", image_size: "cover" },
  { icon: "Skull", title: "Faction Warfare",
    body: "Player-led factions, raids, and shifting alliances. No safe zones.",
    span: "md:col-span-6", icon_color: "#C0A875",
    image: "https://customer-assets-4nw71qhi.emergentagent.net/job_decay-protocol/artifacts/d7jvctrc_factions.jpg",
    image_position: "center", image_size: "cover" },
  { icon: "Radio", title: "Active Staff",
    body: "Tickets answered fast. Events run weekly. No drama, no favorites.",
    span: "md:col-span-12", icon_color: "#C0A875",
    image: "https://images.pexels.com/photos/2599537/pexels-photo-2599537.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image_position: "center", image_size: "cover" },
];

const FALLBACK_HEADER = {
  eyebrow: "// 01 PRINCIPLES",
  title: "Built for survivors",
  desc: "Not a sandbox arena. Not a meta race. A community that values the slow days and the quiet ones — and remembers them.",
};

export default function Features() {
  const [features, setFeatures] = useState(FALLBACK_FEATURES);
  const [header, setHeader] = useState(FALLBACK_HEADER);

  useEffect(() => {
    let cancelled = false;
    api.get("/content/features")
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.features;
        if (Array.isArray(list) && list.length > 0) setFeatures(list);
        if (res.data?.header) setHeader(res.data.header);
      })
      .catch(() => { /* keep fallbacks — public endpoint, best effort */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="relative bg-[#0D110C] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeader
          eyebrow={header.eyebrow}
          title={header.title}
          desc={header.desc}
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-4">
          {features.map((f, i) => {
            const Icon = ICON_MAP[f.icon] || Shield;
            return (
              <div
                key={f.title || i}
                data-testid={`feature-card-${i}`}
                className={`relative overflow-hidden bg-[#161D14] border border-[#283524] p-6 sm:p-8 group hover:border-[#5C6B51] card-lift ${f.span || "md:col-span-6"}`}
              >
                {f.image && (
                  <div
                    className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity bg-no-repeat"
                    style={{
                      backgroundImage: `url(${f.image})`,
                      backgroundSize: f.image_size || "cover",
                      backgroundPosition: f.image_position || "center",
                    }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#161D14] via-transparent to-transparent pointer-events-none" />
                <div className="relative z-10 flex flex-col gap-4 min-h-[160px]">
                  <div className="flex items-center justify-end">
                    <span className="label-tag">0{i + 1}</span>
                  </div>
                  <h3 className="flex items-center gap-3 font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl leading-tight">
                    <Icon
                      className="shrink-0"
                      size={26}
                      strokeWidth={2}
                      style={{ color: f.icon_color || "#C0A875" }}
                    />
                    <span>{f.title}</span>
                  </h3>
                  <p className="text-[#8D9A87] text-sm leading-relaxed max-w-md">{f.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, desc }) {
  const ref = useReveal();
  return (
    <div ref={ref} data-reveal className="max-w-3xl">
      <span className="label-tag">{eyebrow}</span>
      <h2 className="mt-3 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
        {title}
      </h2>
      {desc && (
        <p className="mt-5 text-[#8D9A87] text-base sm:text-lg leading-relaxed max-w-2xl">
          {desc}
        </p>
      )}
    </div>
  );
}
